import { Button } from '@latitude/button';
import type { CustomClassNames } from 'components/ComponentsTypes';
import { useDatepickerContext } from 'hooks/useDatepickerContext';
import type { JSX } from 'react';
import { useState } from 'react';
import { useDayPicker } from 'react-day-picker';

import { PeriodToggle } from './PeriodToggle';
import { TimeSpinner } from './TimeSpinner';
import type { TimePeriod } from './types';
import { clampValue, to12Hour, to24Hour } from './utils';

type TimePickerProps = {
  /** What Cancel restores: the value when the popover opened. */
  valueAtOpen: Date | undefined;
};

export const TimePicker = ({ valueAtOpen }: TimePickerProps): JSX.Element => {
  const { selection, onTimeChange, onOpenChange } = useDatepickerContext();
  const classNames = useDayPicker().classNames as CustomClassNames;
  const selected = selection.mode === 'range' ? undefined : selection.value;

  const initialTime = selected
    ? to12Hour(selected.getHours())
    : { hour: 12, period: 'AM' as TimePeriod };

  const [hour, setHour] = useState(initialTime.hour);
  const [minute, setMinute] = useState(selected ? selected.getMinutes() : 0);
  const [period, setPeriod] = useState<TimePeriod>(initialTime.period);

  // Follow a time committed from elsewhere, such as the text field or the
  // parent. An empty value keeps the last time shown.
  const [syncedTime, setSyncedTime] = useState(selected?.getTime());
  if (selected?.getTime() !== syncedTime) {
    setSyncedTime(selected?.getTime());
    if (selected) {
      const next = to12Hour(selected.getHours());
      setHour(next.hour);
      setMinute(selected.getMinutes());
      setPeriod(next.period);
    }
  }

  const updateTime = (
    newHour: number,
    newMinute: number,
    newPeriod: TimePeriod,
  ) => {
    const newDate = new Date(selected ?? new Date());
    newDate.setHours(to24Hour(newHour, newPeriod), newMinute, 0, 0);
    onTimeChange(newDate);
  };

  // Hour handlers
  const handleHourChange = (value: string) => {
    // Valid hours in 12-hour format: 1-12
    const newHour = clampValue(value, 1, 12);
    setHour(newHour);
    updateTime(newHour, minute, period);
  };

  const increaseHour = () => {
    const newHour = hour === 12 ? 1 : hour + 1;
    setHour(newHour);
    updateTime(newHour, minute, period);
  };

  const decreaseHour = () => {
    const newHour = hour === 1 ? 12 : hour - 1;
    setHour(newHour);
    updateTime(newHour, minute, period);
  };

  // Minute handlers
  const handleMinuteChange = (value: string) => {
    // Valid minutes: 0-59
    const newMinute = clampValue(value, 0, 59);
    setMinute(newMinute);
    updateTime(hour, newMinute, period);
  };

  const increaseMinute = () => {
    const newMinute = minute === 59 ? 0 : minute + 1;
    setMinute(newMinute);
    updateTime(hour, newMinute, period);
  };

  const decreaseMinute = () => {
    const newMinute = minute === 0 ? 59 : minute - 1;
    setMinute(newMinute);
    updateTime(hour, newMinute, period);
  };

  // Period handler
  const togglePeriod = () => {
    const newPeriod: TimePeriod = period === 'AM' ? 'PM' : 'AM';
    setPeriod(newPeriod);
    updateTime(hour, minute, newPeriod);
  };

  const rowHeight = classNames.time_picker_row_height;

  return (
    <div
      className={classNames.time_picker_container}
      role="group"
      aria-label="Time selection"
    >
      <div
        className={classNames.time_picker_row}
        role="group"
        aria-label="Time inputs"
      >
        <TimeSpinner
          value={hour}
          onChange={handleHourChange}
          onBlur={() => handleHourChange(String(hour))}
          onIncrement={increaseHour}
          onDecrement={decreaseHour}
          rowHeight={rowHeight}
          min={1}
          max={12}
          label="Hour"
        />
        <TimeSpinner
          value={minute}
          onChange={handleMinuteChange}
          onBlur={() => handleMinuteChange(String(minute))}
          onIncrement={increaseMinute}
          onDecrement={decreaseMinute}
          rowHeight={rowHeight}
          min={0}
          max={59}
          label="Minute"
        />
        <PeriodToggle
          period={period}
          onToggle={togglePeriod}
          rowHeight={rowHeight}
          daypickerClasses={classNames}
        />
      </div>
      <div
        className={`${classNames.time_picker_row} justify-end`}
        role="group"
        aria-label="Actions"
      >
        <Button
          className={rowHeight}
          variant="neutral"
          htmlType="button"
          aria-label="Cancel time selection"
          onClick={() => {
            onTimeChange(valueAtOpen);
            onOpenChange(false);
          }}
        >
          Cancel
        </Button>
        <Button
          className={rowHeight}
          variant="primary"
          htmlType="button"
          aria-label="Confirm time selection"
          onClick={() => {
            onOpenChange(false);
          }}
        >
          Select
        </Button>
      </div>
    </div>
  );
};
